import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { 
  CreditCard, 
  Users, 
  BookOpen, 
  Receipt, 
  Search, 
  Bell, 
  Settings,
  DollarSign,
  TrendingUp,
  Activity,
  User,
  Calendar,
  BarChart3,
  PieChart,
  FileText,
  ChevronRight,
  Filter,
  ArrowRightCircle,
  Handshake,
  Building,
  RefreshCw,
  Award,
  Plus
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';

import Index from './Index'; // Dashboard de Subscriptions
import InvoicesDashboard from './InvoicesDashboard';
import ClassesDashboard from './ClassesDashboard';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

// Placeholder para os dashboards que ainda não existem
const OpportunitiesDashboard = () => (
  <div className="w-full">
    {/* Cabeçalho moderno */}
    <div className="bg-white dark:bg-gray-950 rounded-xl shadow-sm border p-6 mb-6">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Avatar className="h-16 w-16 border-2">
            <AvatarFallback className="text-2xl bg-primary/10 text-primary">
              <Handshake className="h-8 w-8" />
            </AvatarFallback>
          </Avatar>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-extrabold">Dashboard Comercial</h1>
            </div>
            <p className="text-muted-foreground mt-1">
              Acompanhe oportunidades e desempenho de vendas
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-2 md:mt-0">
          <Button
            className="gap-2"
          >
            <Plus className="h-4 w-4" /> Nova Oportunidade
          </Button>
        </div>
      </div>
    </div>

    {/* Cards de métricas */}
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-extrabold">Total de Oportunidades</CardTitle>
          <TrendingUp className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-extrabold">64</div>
          <p className="text-xs text-muted-foreground">
            +12% em relação ao mês anterior
          </p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-extrabold">Taxa de Conversão</CardTitle>
          <Activity className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-extrabold">38%</div>
          <Progress value={38} className="h-2 mt-2" />
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-extrabold">Receita Prevista</CardTitle>
          <DollarSign className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-extrabold">R$ 482.500</div>
          <p className="text-xs text-muted-foreground">
            Previsão para este trimestre
          </p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-extrabold">Tempo Médio de Conversão</CardTitle>
          <Calendar className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-extrabold">28 dias</div>
          <p className="text-xs text-muted-foreground">
            -3 dias em relação ao trimestre anterior
          </p>
        </CardContent>
      </Card>
    </div>

    {/* Layout de duas colunas */}
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Coluna da esquerda (2/3) */}
      <div className="lg:col-span-2 space-y-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Funil de Vendas</CardTitle>
              <Select defaultValue="month">
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Período" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="week">Esta Semana</SelectItem>
                  <SelectItem value="month">Este Mês</SelectItem>
                  <SelectItem value="quarter">Este Trimestre</SelectItem>
                  <SelectItem value="year">Este Ano</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-[300px] flex items-center justify-center text-muted-foreground">
              [Gráfico de Funil de Vendas]
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Oportunidades Recentes</CardTitle>
              <Button variant="ghost" size="sm">
                Ver todas
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-start justify-between p-3 rounded-lg border hover:bg-muted/30 transition-colors cursor-pointer">
                  <div className="flex gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-primary/10 text-primary">
                        <Building className="h-5 w-5" />
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="font-extrabold">Cliente Potencial {i}</h3>
                      <div className="text-sm text-muted-foreground mt-1">
                        <div className="flex items-center">
                          <DollarSign className="h-3.5 w-3.5 mr-1" />
                          <span>R$ {(Math.random() * 100000).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <Badge className={i % 3 === 0 ? "bg-blue-100 text-blue-800" : i % 3 === 1 ? "bg-yellow-100 text-yellow-800" : "bg-green-100 text-green-800"}>
                      {i % 3 === 0 ? "Qualificação" : i % 3 === 1 ? "Proposta" : "Negociação"}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Coluna da direita (1/3) */}
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Desempenho por Vendedor</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback>V{i}</AvatarFallback>
                      </Avatar>
                      <span className="font-extrabold">Vendedor {i}</span>
                    </div>
                    <span className="text-sm font-extrabold">
                      R$ {(Math.random() * 100000).toFixed(2)}
                    </span>
                  </div>
                  <Progress value={85 - (i * 15)} className="h-2" />
                </div>
              ))}
            </div>
          </CardContent>
          <CardFooter className="border-t px-6 py-4">
            <Button variant="outline" className="w-full">Ver Detalhes</Button>
          </CardFooter>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Próximas Atividades</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-start space-x-3 pb-3 border-b last:border-0 last:pb-0">
                  <div className="min-w-10 text-center">
                    <div className="text-lg font-extrabold">{i + 20}</div>
                    <div className="text-xs text-muted-foreground">Nov</div>
                  </div>
                  <div>
                    <p className="text-sm font-extrabold">
                      {i === 1 ? "Reunião de apresentação" : i === 2 ? "Follow-up por telefone" : "Envio de proposta"}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Cliente Potencial {i+2}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
          <CardFooter className="border-t px-6 py-4">
            <Button variant="outline" className="w-full">Ver Calendário</Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  </div>
);

const UnifiedDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('subscriptions');
  const [notifications, setNotifications] = useState(3);
  const today = new Date();

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };

  return (
    <>
      <Helmet>
        <title>CSM | Trivium - Centro de Gestão</title>
      </Helmet>
      <div className="container mx-auto p-4 md:p-6">
      {/* Cabeçalho principal com ações globais */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <h1 className="text-2xl font-extrabold tracking-tight">Centro de Gestão</h1>
            <Badge variant="outline" className="h-6 px-2 text-xs bg-primary/10 text-primary font-extrabold rounded-full ml-0 sm:ml-2">
              BETA
            </Badge>
          </div>
          <p className="text-muted-foreground">
            {format(today, "EEEE, dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
          </p>
        </div>

        <div className="flex items-center gap-3 self-end md:self-auto">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Buscar..."
              className="w-[150px] sm:w-[200px] pl-8"
            />
          </div>
          
          <div className="relative">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="relative">
                  <Bell className="h-5 w-5" />
                  {notifications > 0 && (
                    <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 text-[10px] font-extrabold text-white flex items-center justify-center">
                      {notifications}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <DropdownMenuLabel>Notificações</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <ScrollArea className="h-[300px]">
                  <div className="space-y-2 p-2">
                    <div className="flex gap-3 items-start p-2 rounded-md hover:bg-muted">
                      <Badge className="bg-blue-100 text-blue-800 mt-0.5">Nova</Badge>
                      <div>
                        <p className="text-sm font-extrabold">Novo cliente registrado</p>
                        <p className="text-xs text-muted-foreground">Cliente XYZ acabou de ser cadastrado no sistema</p>
                        <p className="text-xs text-muted-foreground mt-1">Há 15 minutos atrás</p>
                      </div>
                    </div>
                    <div className="flex gap-3 items-start p-2 rounded-md hover:bg-muted">
                      <Badge className="bg-yellow-100 text-yellow-800 mt-0.5">Alerta</Badge>
                      <div>
                        <p className="text-sm font-extrabold">Fatura próxima do vencimento</p>
                        <p className="text-xs text-muted-foreground">Fatura #1234 vence em 3 dias</p>
                        <p className="text-xs text-muted-foreground mt-1">Há 45 minutos atrás</p>
                      </div>
                    </div>
                    <div className="flex gap-3 items-start p-2 rounded-md hover:bg-muted">
                      <Badge className="bg-green-100 text-green-800 mt-0.5">Sucesso</Badge>
                      <div>
                        <p className="text-sm font-extrabold">Pagamento recebido</p>
                        <p className="text-xs text-muted-foreground">Pagamento de R$ 1.250,00 foi confirmado</p>
                        <p className="text-xs text-muted-foreground mt-1">Há 2 horas atrás</p>
                      </div>
                    </div>
                  </div>
                </ScrollArea>
                <div className="p-2 text-center border-t">
                  <Button variant="link" className="text-sm w-full">Ver todas as notificações</Button>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon">
                <Settings className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Minha Conta</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem>
                <User className="mr-2 h-4 w-4" />
                <span>Perfil</span>
              </DropdownMenuItem>
              <DropdownMenuItem>
                <Settings className="mr-2 h-4 w-4" />
                <span>Configurações</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem>
                <RefreshCw className="mr-2 h-4 w-4" />
                <span>Atualizar dados</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Cartões com resumo geral */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-extrabold">Total de Assinaturas</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-extrabold">42</div>
            <div className="flex items-center space-x-2">
              <div className="flex items-center text-green-500">
                <ArrowRightCircle className="h-3 w-3 rotate-45" />
                <span className="text-xs font-extrabold">+10%</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Comparado ao mês anterior
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-extrabold">Formações Ativas</CardTitle>
            <BookOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-extrabold">18</div>
            <div className="flex items-center space-x-2">
              <div className="flex items-center text-green-500">
                <ArrowRightCircle className="h-3 w-3 rotate-45" />
                <span className="text-xs font-extrabold">+5%</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Comparado ao mês anterior
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-extrabold">Oportunidades em Aberto</CardTitle>
            <Handshake className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-extrabold">24</div>
            <div className="flex items-center space-x-2">
              <div className="flex items-center text-green-500">
                <ArrowRightCircle className="h-3 w-3 rotate-45" />
                <span className="text-xs font-extrabold">+12%</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Comparado ao mês anterior
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-extrabold">Receita Mensal</CardTitle>
            <Receipt className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-extrabold">R$ 148.250</div>
            <div className="flex items-center space-x-2">
              <div className="flex items-center text-green-500">
                <ArrowRightCircle className="h-3 w-3 rotate-45" />
                <span className="text-xs font-extrabold">+8%</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Comparado ao mês anterior
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Abas para os diferentes dashboards */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border mb-6 overflow-hidden">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full" defaultValue={activeTab}>
          <div className="p-2 border-b">
            <TabsList className="w-full grid grid-cols-2 md:grid-cols-4 h-auto gap-2 p-1">
              <TabsTrigger 
                value="subscriptions"
                className="flex items-center justify-center gap-2 py-3 px-3 text-sm"
              >
                <CreditCard className="h-4 w-4" />
                <span>Assinaturas</span>
              </TabsTrigger>
              <TabsTrigger 
                value="classes"
                className="flex items-center justify-center gap-2 py-3 px-3 text-sm"
              >
                <BookOpen className="h-4 w-4" />
                <span>Formações</span>
              </TabsTrigger>
              <TabsTrigger 
                value="opportunities"
                className="flex items-center justify-center gap-2 py-3 px-3 text-sm"
              >
                <Handshake className="h-4 w-4" />
                <span>Comercial</span>
              </TabsTrigger>
              <TabsTrigger 
                value="invoices"
                className="flex items-center justify-center gap-2 py-3 px-3 text-sm"
              >
                <Receipt className="h-4 w-4" />
                <span>Financeiro</span>
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="p-1">
            <TabsContent value="subscriptions" className="py-4 px-1 md:px-4">
              <Index />
            </TabsContent>

            <TabsContent value="classes" className="py-4 px-1 md:px-4">
              <ClassesDashboard />
            </TabsContent>

            <TabsContent value="opportunities" className="py-4 px-1 md:px-4">
              <OpportunitiesDashboard />
            </TabsContent>

            <TabsContent value="invoices" className="py-4 px-1 md:px-4">
              <InvoicesDashboard />
            </TabsContent>
          </div>
        </Tabs>
      </div>

      {/* Footer com informações rápidas */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-4 text-center">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="flex flex-col items-center">
            <div className="flex items-center mb-2">
              <Award className="h-5 w-5 text-primary mr-2" />
              <span className="font-extrabold">Destaques</span>
            </div>
            <p className="text-sm text-muted-foreground">3 formações concluídas</p>
          </div>
          <div className="flex flex-col items-center">
            <div className="flex items-center mb-2">
              <Calendar className="h-5 w-5 text-primary mr-2" />
              <span className="font-extrabold">Próximas Atividades</span>
            </div>
            <p className="text-sm text-muted-foreground">2 reuniões agendadas</p>
          </div>
          <div className="flex flex-col items-center">
            <div className="flex items-center mb-2">
              <BarChart3 className="h-5 w-5 text-primary mr-2" />
              <span className="font-extrabold">Metas do Mês</span>
            </div>
            <p className="text-sm text-muted-foreground">75% alcançado</p>
          </div>
          <div className="flex flex-col items-center">
            <div className="flex items-center mb-2">
              <FileText className="h-5 w-5 text-primary mr-2" />
              <span className="font-extrabold">Relatórios</span>
            </div>
            <p className="text-sm text-muted-foreground">5 novos relatórios</p>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default UnifiedDashboard; 